'use client'

import { useEffect, type ReactNode } from 'react'
import { X } from 'lucide-react'

/**
 * Dialog that renders inline within its container using absolute positioning,
 * instead of portaling to document.body. This keeps dialogs contained within
 * the artist frame in the demo's split-view layout.
 */

interface InlineDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
  className?: string
}

export function InlineDialog({ open, onOpenChange, children, className = '' }: InlineDialogProps) {
  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onOpenChange])

  if (!open) return null

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200 overflow-y-auto"
      style={{ overscrollBehavior: 'contain' }}
      onClick={() => onOpenChange(false)}
      onWheel={(e) => e.stopPropagation()}
    >
      <div
        className={`bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl w-full mx-4 my-auto animate-in zoom-in-95 duration-200 ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}

interface InlineDialogHeaderProps {
  children: ReactNode
  onClose?: () => void
}

export function InlineDialogHeader({ children, onClose }: InlineDialogHeaderProps) {
  return (
    <div className="flex items-start justify-between p-4 border-b border-zinc-800">
      <div className="flex-1 min-w-0">{children}</div>
      {onClose && (
        <button
          onClick={onClose}
          className="p-1 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors shrink-0 ml-2"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

export function InlineDialogBody({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`p-4 ${className}`}>{children}</div>
}

export function InlineDialogFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex justify-end gap-2 p-4 border-t border-zinc-800">
      {children}
    </div>
  )
}

export function InlineDialogTitle({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <h3 className={`text-lg font-semibold text-white ${className}`}>{children}</h3>
}

export function InlineDialogDescription({ children }: { children: ReactNode }) {
  return <p className="text-sm text-zinc-400 mt-1">{children}</p>
}
